import React from "react";
import { SubscriptionSchema as Subscription } from "@pollyslack/hub/src/features/types";
import { EnterpriseGrid } from "@pollyslack/hub/src/types";

import { SubscriptionActionsToolbar } from "../../../components/SubscriptionActionsToolbar";
import { UpdateSubscriptionModal } from "../../../components/UpdateSubscriptionModal";

import {
  setEnterpriseSubActiveState,
  setEnterpriseTier,
} from "../../../services/api/Organization";
import { infoBySlackEnterpriseId } from "../../../services/api/Subscription";

import { PlanParams } from "../../Organization/tabs/PlanParams";
import { decryptEnterpriseGridToken } from "../../Organization/tabs/helpers";
import { EnterpriseDetails } from "../../../pages/Organization/components/EnterpriseDetails";
import { SubscriptionDetails } from "../../Subscription/components/SubscriptionDetails";
import { Spinner } from "reactstrap";

type EnterpriseTabProps = {
  grid: EnterpriseGrid;
  reload: () => void;
};

export const EnterpriseTab: React.FunctionComponent<EnterpriseTabProps> = (
  props: EnterpriseTabProps,
) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [decryptedToken, setDecryptedToken] = React.useState<string>();
  const [subscription, setSubscription] = React.useState<Subscription>();
  const [reloadToggle, setReloadToggle] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      let subscription: Subscription;
      try {
        subscription = await infoBySlackEnterpriseId({
          id: props.grid.slackEnterpriseId,
        });
      } catch (e) {
        // not found? that's ok
      }
      if (!cancelled) {
        setSubscription(subscription);
      }
      setIsLoading(false);
    }
    load();
    return function cleanup() {
      cancelled = true;
    };
  }, [reloadToggle, props.grid.slackEnterpriseId]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center pt-5">
        <Spinner style={{ width: "3rem", height: "3rem" }} />
        <div className="pl-2" style={{ fontSize: "2rem" }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <>
      <EnterpriseDetails
        enterprise={props.grid}
        decryptedSlackBotToken={decryptedToken}
        onTokenDecrypt={async () =>
          setDecryptedToken(
            await decryptEnterpriseGridToken(props.grid.slackEnterpriseId),
          )
        }
      />
      {subscription && (
        <>
          <h3 className="mt-4">Enterprise Subscription</h3>
          <SubscriptionActionsToolbar
            onUpdateSubscriptionClick={() => setModalOpen(true)}
            onDeactivateClick={async () => {
              if (subscription.active) {
                await enterpriseSubSetActive(subscription._id, false);
                setReloadToggle(!reloadToggle);
                setModalOpen(false);
              }
            }}
            onReactivateClick={async () => {
              if (!subscription.active) {
                await enterpriseSubSetActive(subscription._id, true);
                setReloadToggle(!reloadToggle);
                setModalOpen(false);
              }
            }}
          />
          <UpdateSubscriptionModal
            isOpen={modalOpen}
            subscription={subscription}
            onClose={() => {
              setModalOpen(false);
            }}
            onSubmit={async (params) => {
              await setEnterprisePlan(props.grid, params);
              setModalOpen(false);
              setReloadToggle(!reloadToggle);
            }}
          />
          <SubscriptionDetails subscription={subscription} />
        </>
      )}
    </>
  );
};

async function enterpriseSubSetActive(subscriptionId: string, active: boolean) {
  try {
    await setEnterpriseSubActiveState({
      _id: subscriptionId,
      active,
    });
  } catch (e) {
    alert("Error: " + e);
  }
}

async function setEnterprisePlan(grid: EnterpriseGrid, params: PlanParams) {
  try {
    await setEnterpriseTier({
      slackEnterpriseId: grid.slackEnterpriseId,
      orgId: "", // Not needed for enterprise-level subscriptions
      ...params,
    });
  } catch (e) {
    alert("Error: " + e);
  }
}
